import type { ConsultationBucket, ExpertRequestBucket, ProjectBucket } from './useDashboardData';
import type { RangePreset } from './useDateRange';

// Pure config for the 8-card Account Overview radiogroup (ENG-5256). Maps each
// metric to its live `group.stats` scalar, its timeseries series accessor, a
// unit label, and a "lens" that shapes the chart subtitle. No JSX lives here —
// Task 4B composes these into cards + the activity chart.

/**
 * Stable identifier for each Account Overview metric. The money slot is
 * polymorphic: credit dashboards bind `credits-spent`, SMB dashboards bind
 * `amount-spent`. Only one ever occupies the first Activity card at a time, so
 * the visible card count stays at 8.
 */
export type MetricId =
  | 'credits-spent'
  | 'amount-spent'
  | 'projects'
  | 'expert-requests'
  | 'experts-engaged'
  | 'completed'
  | 'reviewed'
  | 'pending-reviews'
  | 'avg-rating';

/**
 * Bucket spacing of the single activity chart. The API picks granularity by
 * span (≤32d → daily, ≤130d → weekly, else monthly) and shares it across every
 * series, so all metrics in the chart render at one cadence.
 */
export type Cadence = 'daily' | 'weekly' | 'monthly';

/**
 * How a metric's chart subtitle reads.
 * - `settled`: cumulative work that has happened — "{total} {unit} · {adverb}".
 * - `snapshot`: a still-open count viewed by origination — "{count} still
 *   pending · by origination {noun}". Pending Reviews only.
 */
export type MetricLens = 'settled' | 'snapshot';

/** A point on the chart: the bucket start plus the metric's value (null = gap). */
export interface MetricSeriesPoint {
  periodStart: string;
  value: number | null;
}

interface MetricLensConfig {
  /** Card label, echoed verbatim by the chart heading. */
  label: string;
  /** Field on the resolved `GroupStats` backing the live card value. */
  statField: keyof MetricStatFields;
  /** Subtitle lens. */
  lens: MetricLens;
  /** Unit phrase used in the subtitle (and chart aria-label). */
  unit: string;
  /** Which timeseries this metric reads from. */
  series: 'consultation' | 'expertRequests' | 'projects';
  /** Pulls this metric's value out of a single timeseries bucket. */
  point: (bucket: ConsultationBucket | ExpertRequestBucket | ProjectBucket) => number | null;
}

/**
 * The subset of `GroupStats` scalar fields the cards bind to. Kept as a local
 * shape (not an import of the full hook return) so this config stays pure and
 * decoupled from the data layer's loading/error machinery.
 */
export interface MetricStatFields {
  creditsSpentCents: number;
  consultationSpendCents: number;
  projectsCreated: number;
  expertRequests: number;
  expertsEngaged: number;
  consultationsCompleted: number;
  consultationsReviewed: number;
  consultationsPending: number;
  averageRating: number | null;
}

type AnyBucket = ConsultationBucket | ExpertRequestBucket | ProjectBucket;

function asConsultation(bucket: AnyBucket): ConsultationBucket {
  return bucket as ConsultationBucket;
}

function asExpertRequest(bucket: AnyBucket): ExpertRequestBucket {
  return bucket as ExpertRequestBucket;
}

function asProject(bucket: AnyBucket): ProjectBucket {
  return bucket as ProjectBucket;
}

export const METRIC_LENS: Record<MetricId, MetricLensConfig> = {
  'credits-spent': {
    label: 'Credits Spent',
    statField: 'creditsSpentCents',
    lens: 'settled',
    unit: 'credits spent',
    series: 'consultation',
    point: (b) => asConsultation(b).creditsCents,
  },
  'amount-spent': {
    label: 'Expert Spend',
    statField: 'consultationSpendCents',
    lens: 'settled',
    unit: 'spent',
    series: 'consultation',
    point: (b) => asConsultation(b).consultationSpendCents,
  },
  projects: {
    label: 'Projects Created',
    statField: 'projectsCreated',
    lens: 'settled',
    unit: 'projects',
    series: 'projects',
    point: (b) => asProject(b).count,
  },
  'expert-requests': {
    label: 'Expert Requests',
    statField: 'expertRequests',
    lens: 'settled',
    unit: 'requests',
    series: 'expertRequests',
    point: (b) => asExpertRequest(b).count,
  },
  'experts-engaged': {
    label: 'Experts Engaged',
    statField: 'expertsEngaged',
    lens: 'settled',
    unit: 'experts engaged',
    series: 'consultation',
    point: (b) => asConsultation(b).experts,
  },
  completed: {
    label: 'Completed',
    statField: 'consultationsCompleted',
    lens: 'settled',
    unit: 'completed',
    series: 'consultation',
    point: (b) => asConsultation(b).total,
  },
  reviewed: {
    label: 'Reviewed',
    statField: 'consultationsReviewed',
    lens: 'settled',
    unit: 'reviewed',
    series: 'consultation',
    point: (b) => asConsultation(b).reviewed,
  },
  'pending-reviews': {
    label: 'Pending Reviews',
    statField: 'consultationsPending',
    lens: 'snapshot',
    unit: 'still pending',
    series: 'consultation',
    point: (b) => asConsultation(b).pending,
  },
  'avg-rating': {
    label: 'Avg Rating',
    statField: 'averageRating',
    lens: 'settled',
    unit: 'avg',
    // averageRating is null for empty buckets — surfaced as a gap, not a zero bar.
    series: 'consultation',
    point: (b) => asConsultation(b).averageRating,
  },
};

/**
 * The metric occupying the first Activity slot, by dashboard type. Credit
 * dashboards spend OFC credits; SMB dashboards spend USD. Exactly one is ever
 * shown, so the visible card count is always 8.
 */
export function moneyMetricFor(isSMB: boolean): MetricId {
  return isSMB ? 'amount-spent' : 'credits-spent';
}

/** The 4 Activity-row ids for a money slot (Consultations row is fixed). */
export function activityIds(moneyMetric: MetricId): MetricId[] {
  return [moneyMetric, 'projects', 'expert-requests', 'experts-engaged'];
}

/** The 4 fixed Consultations-row ids. */
export const CONSULTATION_IDS: MetricId[] = [
  'completed',
  'reviewed',
  'pending-reviews',
  'avg-rating',
];

/**
 * All metric ids in card order (Activity row, then Consultations row) for a
 * given money slot. Drives the roving-radiogroup order + the per-card record.
 */
export function metricIds(moneyMetric: MetricId): MetricId[] {
  return [...activityIds(moneyMetric), ...CONSULTATION_IDS];
}

/** Default 8-card order (credit dashboards). SMB swaps the money slot. */
export const METRIC_IDS: MetricId[] = metricIds('credits-spent');

const CADENCE_ADVERB: Record<Cadence, string> = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
};

const CADENCE_NOUN: Record<Cadence, string> = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
};

/** "daily" / "weekly" / "monthly" — used by the settled lens. */
export function cadenceAdverb(cadence: Cadence): string {
  return CADENCE_ADVERB[cadence];
}

/** "day" / "week" / "month" — used by the snapshot lens + caption. */
export function cadenceNoun(cadence: Cadence): string {
  return CADENCE_NOUN[cadence];
}

/**
 * The cadence a range resolves to, mirroring the API's span-based bucketing
 * (≤32d daily, ≤130d weekly, else monthly). 30d → daily, 90d → weekly, 12m and
 * All → monthly. Exported so Task 4B can derive cadence from the active preset.
 */
export function cadenceFromRange(preset: RangePreset): Cadence {
  switch (preset) {
    case '30d':
      return 'daily';
    case '90d':
      return 'weekly';
    case '12m':
    case 'all':
    default:
      return 'monthly';
  }
}

/** Formats a rating mean to one decimal place (e.g. 4.2). */
export function formatAvgRating(value: number | null): string {
  if (value === null) return '—';
  return value.toFixed(1);
}

/** Formats a whole-dollar amount with a "$" and thousands separators (e.g. "$1,250"). */
export function formatDollars(dollars: number): string {
  return `$${Math.round(dollars).toLocaleString('en-US')}`;
}

/**
 * Converts a metric's raw `group.stats`/timeseries scalar into the numeric value
 * shown to the user. The two money metrics are stored in pip (cents) and divide
 * by 100 once — Credits Spent → OFC credits, Amount Spent → USD dollars; every
 * other metric passes through unchanged. Credits keep ONE decimal (ENG-5289):
 * consultations commonly cost fractional credits (0.65), so whole-credit
 * rounding erased sub-credit months from the chart and drifted the card from
 * the Credits-page ledger. Dollars stay whole. Centralizing the scale here
 * keeps the card value, subtitle, and chart series in agreement — a single
 * source of truth so the pip→display conversion can never drift between them.
 * Null is preserved (Avg Rating's empty-bucket gap).
 */
export function metricDisplayValue(metricId: MetricId, raw: number | null): number | null {
  if (raw === null) return null;
  if (metricId === 'credits-spent') return Math.round(raw / 10) / 10;
  if (metricId === 'amount-spent') return Math.round(raw / 100);
  return raw;
}

/**
 * The card big-value string for a metric, given its display value. Amount Spent
 * formats as a dollar amount ("$1,250"); every other metric renders the bare
 * number. Avg Rating is handled separately by the card (★ suffix), so it isn't
 * routed here.
 */
export function formatMetricValue(metricId: MetricId, display: number | null): string {
  if (metricId === 'amount-spent') return formatDollars(display ?? 0);
  return `${display ?? 0}`;
}

/**
 * The chart subtitle for a metric, given its live value and the active cadence.
 *
 * - settled → "{value} {unit} · {adverb}". Avg Rating renders the 1-decimal mean;
 *   Amount Spent renders a dollar amount.
 * - snapshot → "{value} still pending · by origination {noun}" (Pending Reviews).
 */
export function metricSubtitle(metricId: MetricId, value: number | null, cadence: Cadence): string {
  const config = METRIC_LENS[metricId];

  if (config.lens === 'snapshot') {
    const count = value ?? 0;
    return `${count} still pending · by origination ${cadenceNoun(cadence)}`;
  }

  if (metricId === 'avg-rating') {
    return `${formatAvgRating(value)} ${config.unit} · ${cadenceAdverb(cadence)}`;
  }

  if (metricId === 'amount-spent') {
    return `${formatDollars(value ?? 0)} ${config.unit} · ${cadenceAdverb(cadence)}`;
  }

  const total = value ?? 0;
  return `${total} ${config.unit} · ${cadenceAdverb(cadence)}`;
}

/**
 * Muted caption shown only under the snapshot (Pending Reviews) chart,
 * explaining why recent buckets look heavier.
 */
export function snapshotCaption(cadence: Cadence): string {
  const noun = cadenceNoun(cadence);
  return `Recent ${noun}s show more open reviews because older ones have been worked down.`;
}
